<template>
  <div v-show="showCarousel" class="product-carousel critical-component-clear">
    <div class="product-carousel__container">
      <div class="product-carousel__header">
        <div class="product-carousel__header-text">
          <h2
            v-if="liquid.sectionTitle"
            class="product-carousel__title text-mobile-headlines-h3 text-desktop-headline-h3"
            v-text="liquid.sectionTitle"
          />

          <p
            v-if="liquid.sectionSubtitle"
            class="text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"
            v-text="liquid.sectionSubtitle"
          />
        </div>

        <div
          v-if="liquid.sectionType === 'carousel'"
          class="product-carousel__arrows splide__arrows"
        >
          <button
            class="splide__arrow splide__arrow--prev"
            type="button"
            @click="goPrev"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.previous')"
            />

            <svg
              width="29"
              height="15"
              viewBox="0 0 29 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 0.29124C19.8137 0.478602 19.7092 0.732054 19.7092 0.99624C19.7092 1.26043 19.8137 1.51388 20 1.70124L24.56 6.34124L1 6.34124C0.734784 6.34124 0.48043 6.4466 0.292893 6.63413C0.105357 6.82167 0 7.07602 0 7.34124C0 7.60646 0.105357 7.86081 0.292893 8.04835C0.48043 8.23588 0.734784 8.34124 1 8.34124L24.53 8.34124L20 12.8712C19.9063 12.9642 19.8319 13.0748 19.7811 13.1967C19.7303 13.3185 19.7042 13.4492 19.7042 13.5812C19.7042 13.7133 19.7303 13.844 19.7811 13.9658C19.8319 14.0877 19.9063 14.1983 20 14.2912C20.1874 14.4775 20.4408 14.582 20.705 14.582C20.9692 14.582 21.2226 14.4775 21.41 14.2912L27.78 7.92124C27.9479 7.75257 28.0422 7.52425 28.0422 7.28624C28.0422 7.04823 27.9479 6.81991 27.78 6.65124L21.41 0.29124C21.2226 0.104989 20.9692 0.000445366 20.705 0.000445366C20.4408 0.000445366 20.1874 0.104989 20 0.29124Z"
                fill="#3B3935"
              />
            </svg>
          </button>

          <button
            class="splide__arrow splide__arrow--next"
            type="button"
            @click="goNext"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.next')"
            />

            <svg
              width="29"
              height="15"
              viewBox="0 0 29 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 0.29124C19.8137 0.478602 19.7092 0.732054 19.7092 0.99624C19.7092 1.26043 19.8137 1.51388 20 1.70124L24.56 6.34124L1 6.34124C0.734784 6.34124 0.48043 6.4466 0.292893 6.63413C0.105357 6.82167 0 7.07602 0 7.34124C0 7.60646 0.105357 7.86081 0.292893 8.04835C0.48043 8.23588 0.734784 8.34124 1 8.34124L24.53 8.34124L20 12.8712C19.9063 12.9642 19.8319 13.0748 19.7811 13.1967C19.7303 13.3185 19.7042 13.4492 19.7042 13.5812C19.7042 13.7133 19.7303 13.844 19.7811 13.9658C19.8319 14.0877 19.9063 14.1983 20 14.2912C20.1874 14.4775 20.4408 14.582 20.705 14.582C20.9692 14.582 21.2226 14.4775 21.41 14.2912L27.78 7.92124C27.9479 7.75257 28.0422 7.52425 28.0422 7.28624C28.0422 7.04823 27.9479 6.81991 27.78 6.65124L21.41 0.29124C21.2226 0.104989 20.9692 0.000445366 20.705 0.000445366C20.4408 0.000445366 20.1874 0.104989 20 0.29124Z"
                fill="#3B3935"
              />
            </svg>
          </button>
        </div>
      </div>

      <div
        v-if="liquid.sectionType === 'carousel'"
        class="product-carousel__splide splide"
        :js-section-id="liquid.sectionId"
      >
        <div class="splide__track carousel">
          <div
            ref="scrollableList"
            class="product-carousel__list carousel splide__list"
          >
            <div
              v-for="(product, index) of products"
              :key="index"
              :class="itemClass"
            >
              <product-card
                :is-variant="isVariant"
                :product="product"
                :variant="product.selectedOrFirstAvailableVariant"
                location="Section Product Carousel"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-else class="product-carousel__splide">
        <div class="product-carousel__list grid">
          <div
            v-for="(product, index) of products"
            :key="index"
            :class="itemClass"
          >
            <product-card
              :is-variant="isVariant"
              :lazyload="index > 4"
              :product="product"
              :variant="product.selectedOrFirstAvailableVariant"
              location="Section Product Carousel"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Product carousel (product-carousel)
 * -----------------------------------------------------------------------------
 * A multipurpose product carousel.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.sectionType Product Section's type.
 * @param {String} liquid.sectionTitle - Section setting title.
 * @param {String} liquid.productId - Product ID.
 * @param {String} liquid.source - Product source.
 * @param {String} liquid.metaobject - Metaobject handle referencing products.
 * @param {Array}  liquid.lastOrderedProducts - Last ordered products.
 * @param {Number} liquid.limit - Product limit.
 *
 */
import { Splide } from '@splidejs/splide';
import { mapState } from 'pinia';

import ProductCard from '~global/product-card/product-card';

import recommendedProductsQuery from '~graphql/recommended-products.gql';
import productCarouselQuery from '~graphql/product-carousel.gql';
import nodesByIdQuery from '~graphql/nodes-by-id-product-card.gql';

import { formatCarouselProducts } from '~/format/format-carousel-products';
import { formatTrackingItem } from '~/format/format-tracking-item';

import { transformId } from '~/helpers/graphql';
import { computeDiscountLogic } from '~/helpers/vue';
import timings from '~/config/timings';

import { values as breakpoints } from '~/config/breakpoints';
import useLastViewedProductsStore from '~stores/last-viewed-products';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import useIndexStore from '~stores/index';

export default {
  name: 'ProductCarousel',

  components: {
    ProductCard,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        lastOrderedProducts: [],
        limit: 12,
        metaobject: '',
        placeholderCount: 8,
        productId: '',
        sectionId: '',
        sectionSubtitle: '',
        sectionTitle: '',
        sectionType: '',
        source: '',
        hideSoldout: false,
      }),
    },
  },

  data() {
    return {
      carousel: null,
      carouselRef: null,
      collection: null,
      isVariant: false,
      products: Array.from(
        { length: this.liquid.placeholderCount },
        () => false,
      ),
      state: 'loading',
    };
  },

  computed: {
    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
    }),
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
      discountCode: 'code',
    }),
    ...mapState(useLastViewedProductsStore, {
      viewedProducts: (state) => state.products,
    }),

    /**
     * Map Pinia state.
     */

    ...mapState(useLastViewedProductsStore, {
      viewedProducts: (state) => state.products,
    }),

    /**
     * Compute product item class.
     * @returns {String}
     */
    itemClass() {
      const baseClass = 'product-carousel__product';

      if (this.liquid.sectionType === 'carousel') {
        return `${baseClass} splide__slide`;
      }

      return `${baseClass} product-carousel__card`;
    },

    /**
     * Compute query type.
     * @returns {String}
     */
    queryType() {
      if (this.liquid.source === 'recommendations') {
        return recommendedProductsQuery;
      }

      if (
        (this.liquid.source === 'last-ordered' &&
          this.liquid.lastOrderedProducts.length >= 1) ||
        (this.liquid.source === 'last-viewed' &&
          this.viewedProducts.length >= 4)
      ) {
        return nodesByIdQuery;
      }

      return productCarouselQuery;
    },

    /**
     * Compute query variables.
     * @returns {Object}
     */
    queryVariables() {
      const { source, productId, limit, metaobject, lastOrderedProducts } =
        this.liquid;

      if (source === 'recommendations' && productId) {
        return {
          country: this.$variable('store.country'),
          intent: 'RELATED',
          language: this.$variable('store.language'),
          productId: transformId(productId, 'Product'),
        };
      }

      if (source === 'last-viewed' && this.viewedProducts.length >= 4) {
        return {
          country: this.$variable('store.country'),
          ids: this.viewedProducts.map((id) => transformId(id, 'Product')),
          language: this.$variable('store.language'),
        };
      }

      if (
        source === 'last-ordered' &&
        this.$variable('customer.loggedIn') &&
        lastOrderedProducts.length >= 1
      ) {
        return {
          country: this.$variable('store.country'),
          ids: lastOrderedProducts
            .slice(0, limit)
            .map((id) => transformId(id, 'ProductVariant')),
          language: this.$variable('store.language'),
        };
      }

      return {
        country: this.$variable('store.country'),
        handle: {
          handle: metaobject,
          type: 'product_variant_list',
        },
        language: this.$variable('store.language'),
        limit,
      };
    },

    /**
     * Compute whether to show carousel.
     * - Hide 'last-seen' carousel by default.
     * @returns {Boolean}
     */
    showCarousel() {
      if (this.state === 'loading' && this.liquid.source === 'last-viewed') {
        return false;
      }

      return this.products.length > 0;
    },
  },

  mounted() {
    const { source, lastOrderedProducts } = this.liquid;

    if (
      (source === 'last-ordered' && lastOrderedProducts.length < 1) ||
      (source === 'last-viewed' && this.viewedProducts.length < 4)
    ) {
      this.products = [];
      return;
    }

    this.carouselRef = `#vue-element [js-section-id="${this.liquid.sectionId}"]`;
    this.fetchProducts();
  },

  methods: {
    /**
     * Construct the carousel.
     */
    constructCarousel() {
      if (!this.carouselRef) {
        return;
      }

      this.carousel = new Splide(this.carouselRef, {
        arrows: false,
        drag: true,
        focus: false,
        gap: 'var(--layout-gutter)',
        mediaQuery: 'min',
        omitEnd: true,
        pagination: false,
        perMove: 2,
        perPage: 2,
        slideFocus: false,
        snap: true,
        speed: this.$timing('normal'),
        type: 'slide',
        breakpoints: {
          [breakpoints.l]: {
            arrows: false,
            perPage: 3,
            drag: true,
          },
        },
      });

      setTimeout(() => {
        this.carousel.mount();
      }, timings.normal);
    },

    /**
     * Fetch products based on source.
     */
    async fetchProducts() {
      const response = await cnvs.Query({
        query: this.queryType,
        variables: this.queryVariables,
      });

      const { products, collection, isVariant } = formatCarouselProducts(
        response,
        this.liquid.limit,
      );

      if (this.$variable('settings.disableComponentReadyState')) {
        return;
      }

      this.products = products.filter((product) => {
        const availableState = product.selectedOrFirstAvailableVariant
          ? product.selectedOrFirstAvailableVariant.availableState
          : product.availableState;
        return (
          product.availableState !== 'hidden' &&
          !(this.liquid.hideSoldout && availableState !== 'available') &&
          `${product?.variants[0]?.id}` !==
            this.$variable('product.depositProductID') &&
          product.hiddenProduct?.value !== 'true'
        );
      });

      if (collection) {
        this.collection = collection;
      }

      if (isVariant) {
        this.isVariant = isVariant;
      }

      // Track product list view
      this.trackProductListView();

      this.state = 'ready';

      /** isMobile is necessary to properly calculate the width of section */
      if (!this.isMobile && this.liquid.sectionType === 'carousel') {
        await this.$nextTick();
        this.constructCarousel();
      }
    },

    /**
     * Track product list view
     * - Need to send collection title, id and the product list
     */
    trackProductListView() {
      cnvs.EventBus.emit('dataLayer:view_item_list', {
        collection:
          this.collection?.handle ||
          this.liquid.sectionTitle ||
          this.liquid.source ||
          '-',
        collectionId: this.collection?.id || this.liquid.id || '-',
        products: this.products.map((product, index) => {
          const price =
            product.selectedOrFirstAvailableVariant.price?.amount || 0;
          const compareAtPrice =
            product.selectedOrFirstAvailableVariant.compareAtPrice?.amount || 0;
          const discountLogic = computeDiscountLogic({
            discountPreview: false,
            onSale: compareAtPrice > price,
            product,
            variant: product.selectedOrFirstAvailableVariant,
          });
          const percentageDiscount = this.getCampaignDiscount(
            discountLogic,
            'active',
          );
          const discountObject = {
            percentageDiscount,
            discountCode: this.discountCode,
          };
          const deliveryTime = this.$string('product.delivery_time', {
            replace: { time: this.$variable('product.deliveryTime') },
          });
          const currencyCode = this.$variable('store.currency');

          return formatTrackingItem({
            index,
            product,
            variant: product.selectedOrFirstAvailableVariant,
            collection: this.collection,
            template: this.liquid.source,
            discountObject,
            deliveryTime,
            currencyCode,
          });
        }),
      });
    },

    /**
     * Get the scrollable list element.
     * @returns {HTMLElement|null}
     */
    getScrollableList() {
      if (this.$refs.scrollableList) {
        return this.$refs.scrollableList;
      }

      // Fallback: find by selector
      const selector = `#vue-element [js-section-id="${this.liquid.sectionId}"] .product-carousel__list`;
      return document.querySelector(selector);
    },

    /**
     * Calculate scroll amount based on card width and gap.
     * @returns {Number}
     */
    getScrollAmount() {
      const scrollableList = this.getScrollableList();
      if (!scrollableList) {
        return 0;
      }

      const firstCard = scrollableList.querySelector('.splide__slide');
      if (!firstCard) {
        return 0;
      }

      const cardWidth = firstCard.offsetWidth;
      const computedStyle = getComputedStyle(scrollableList);
      const gapValue = computedStyle.gap || computedStyle.columnGap || '0';

      // Parse gap value (could be in px, rem, etc.)
      let gap = 0;
      if (gapValue.includes('px')) {
        gap = parseFloat(gapValue);
      } else if (gapValue.includes('rem')) {
        const remValue = parseFloat(gapValue);
        gap =
          remValue *
          parseFloat(getComputedStyle(document.documentElement).fontSize);
      } else if (gapValue.includes('var')) {
        // Try to get CSS variable value
        const rootStyle = getComputedStyle(document.documentElement);
        const gutterValue = rootStyle.getPropertyValue('--layout-gutter');
        if (gutterValue) {
          gap = parseFloat(gutterValue) || 0;
        }
      } else {
        gap = parseFloat(gapValue) || 0;
      }

      return cardWidth + gap;
    },

    /**
     * Go to previous slide using native scroll.
     */
    goPrev() {
      const scrollableList = this.getScrollableList();
      if (!scrollableList) {
        return;
      }

      const scrollAmount = this.getScrollAmount();
      if (scrollAmount === 0) {
        return;
      }

      scrollableList.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      });
    },

    /**
     * Go to next slide using native scroll.
     */
    goNext() {
      const scrollableList = this.getScrollableList();
      if (!scrollableList) {
        return;
      }

      const scrollAmount = this.getScrollAmount();
      if (scrollAmount === 0) {
        return;
      }

      scrollableList.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });
    },
  },
};
</script>

<style lang="scss">
@import '@/components/splide';
@import './product-carousel';
</style>
